/**
 * LightboxOverlay - Fullscreen image preview
 *
 * Overlay with image, metadata, and close on click/Escape.
 */

import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import type { ProductAsset } from '@/types/products'

interface LightboxOverlayProps {
  image: ProductAsset | null
  onClose: () => void
}

export function LightboxOverlay({ image, onClose }: LightboxOverlayProps) {
  const { t } = useTranslation()
  if (!image?.asset_id) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 cursor-pointer"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      tabIndex={0}
      role="dialog"
      aria-label="Bildansicht"
    >
      <Button
        variant="ghost"
        className="absolute top-4 right-4 z-10 bg-white text-neutral-900 hover:bg-neutral-100"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <X className="h-6 w-6" />
      </Button>

      <div className="relative">
        <img
          src={`/api/v1/asset/${image.asset_id}/image`}
          alt={image.alt_text || `Bild Slot ${image.slot}`}
          className="max-w-[90vw] max-h-[90vh] object-contain cursor-default"
          onClick={(e) => e.stopPropagation()}
        />
        <ImageInfoBar image={image} />
      </div>
    </div>
  )
}

function ImageInfoBar({ image }: { image: ProductAsset }) {
  const { t } = useTranslation()
  return (
    <div className="absolute bottom-4 left-4 right-4 bg-black/75 text-white p-4 rounded-lg">
      <div className="flex items-center gap-4">
        <div>
          <span className="font-semibold">Slot #{image.slot}</span>
          {image.channel_name && (
            <span className="ml-2 text-primary-300">&bull; {image.channel_name}</span>
          )}
          {!image.channel_id && (
            <span className="ml-2 text-neutral-300">&bull; {t('products.allChannels')}</span>
          )}
        </div>
        {image.width && image.height && (
          <div className="text-sm text-neutral-300">
            {image.width} &times; {image.height}
          </div>
        )}
      </div>
      {image.alt_text && (
        <div className="mt-2 text-sm text-neutral-300">{image.alt_text}</div>
      )}
    </div>
  )
}
